.flip {
  --fc-halo: #f6d98f;
  --fc-line: #e8b04a;
  position: relative;
  flex: none;
  width: var(--w, 180px);
  aspect-ratio: 180 / 314;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  font: inherit;
  -webkit-tap-highlight-color: transparent;
}

.tappable {
  cursor: pointer;
}

.wait .lift {
  transform: translateY(-6px) scale(1.02);
}

.float {
  position: absolute;
  inset: 0;
  animation: bob 4.2s ease-in-out infinite;
}

.go .float,
.done .float {
  animation: none;
}

@keyframes bob {
  50% {
    transform: translateY(-6px);
  }
}

.lift {
  position: absolute;
  inset: 0;
  transition: transform 0.9s cubic-bezier(0.45, 0, 0.25, 1);
}

.scene {
  position: absolute;
  inset: 0;
  perspective: 1000px;
}

.card {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  transition: transform 1s cubic-bezier(0.45, 0.05, 0.25, 1);
}

.open .card {
  transform: rotateY(180deg);
}

.static .card {
  transition: none;
  transform: none !important;
}

.static .front {
  transform: none !important;
}

.static .back,
.done .back {
  visibility: hidden;
}

.face {
  position: absolute;
  inset: 0;
  margin: auto;
  border-radius: calc(var(--w, 180px) * 0.06);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
}

.front {
  transform: rotateY(180deg);
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
}

.faceArt {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: auto;
}

.fitted {
  border-radius: calc(var(--w, 180px) * 0.06);
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
}

.fitted::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  border: 1px solid color-mix(in srgb, var(--fc-line) 55%, transparent);
  pointer-events: none;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.faceArt img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.frontFallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  background: linear-gradient(180deg, var(--surface), var(--bg));
  border: 1px solid var(--accent);
  color: var(--accent);
  font-family: var(--font-display, Georgia, serif);
  text-align: center;
  padding: 12px;
  border-radius: calc(var(--w, 180px) * 0.06);
}

.sheen {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0;
}

.back {
  transform: rotateY(0deg);
  --k1: #2a1d10;
  --k2: #0f0a05;
  --ink: #e8b04a;
  color: var(--ink);
  background: radial-gradient(120% 80% at 50% 38%, var(--k1), var(--k2));
  border: 1px solid color-mix(in srgb, var(--ink) 55%, transparent);
}

.deckBack {
  position: absolute;
  inset: 0;
  z-index: 1;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.back.imageBack {
  background: none;
  border: 0;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
}

.back.imageBack::after {
  content: none;
}

.face *,
.back::after {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.back::after {
  content: '';
  position: absolute;
  inset: 4%;
  border: 1px solid color-mix(in srgb, var(--ink) 35%, transparent);
  border-radius: calc(var(--w, 180px) * 0.04);
  pointer-events: none;
}

.art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.fx {
  position: absolute;
  left: -70%;
  top: -40%;
  width: 240%;
  height: 180%;
  pointer-events: none;
  z-index: 5;
}

.halo,
.rays,
.flash,
.ring,
.mist,
.bridge {
  position: absolute;
  pointer-events: none;
  opacity: 0;
}

.halo {
  inset: -22% -50%;
  background: radial-gradient(closest-side, var(--glow), transparent);
  z-index: -1;
}

.rays {
  inset: -45% -90%;
  z-index: -1;
  background: repeating-conic-gradient(from 0deg, color-mix(in srgb, var(--accent) 34%, transparent) 0 6deg, transparent 6deg 20deg);
  mask: radial-gradient(closest-side, #000 20%, transparent 75%);
}

.flash {
  inset: -65% -110%;
  background: radial-gradient(closest-side, rgba(255, 250, 230, 0.95), rgba(255, 220, 140, 0.4) 40%, transparent 70%);
  z-index: 4;
}

.ring {
  left: 50%;
  top: 50%;
  width: 60px;
  height: 60px;
  margin: -30px 0 0 -30px;
  border-radius: 50%;
  border: 3px solid var(--accent-soft);
  z-index: 3;
  box-shadow: 0 0 18px var(--accent);
}


.bridge {
  inset: -24% -55%;
  border-radius: 50%;
  background: radial-gradient(closest-side, color-mix(in srgb, var(--fc-halo) 70%, transparent), transparent);
  z-index: -1;
}

.lit .bridge {
  animation: bridge 1.8s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

@keyframes bridge {
  0% {
    opacity: 0;
    transform: scale(1.35);
  }
  16% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}

.mist {
  inset: -20% -45%;
  border-radius: 50%;
  background: radial-gradient(closest-side, color-mix(in srgb, var(--text) 22%, transparent), transparent);
  filter: blur(10px);
  z-index: 2;
}

@keyframes halo {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  35% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.25);
  }
}

@keyframes flash {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  25% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.2);
  }
}

@keyframes ring {
  0% {
    opacity: 1;
    transform: scale(0.4);
  }
  100% {
    opacity: 0;
    transform: scale(7);
    border-width: 1px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}




:global(:root[data-theme='light']) .flip { --fc-halo: #e8a93a; --fc-line: #8a6a3f; }

.v1 .back { --k1: #1f1710; --k2: #120d08; --ink: #f6d98f; }
.v2 .back { --k1: #18203a; --k2: #080b17; --ink: #f6d98f; }
.v3 .back { --k1: #3e200c; --k2: #150a04; --ink: #ffcf73; }
.v4 .back { --k1: #10292a; --k2: #051110; --ink: #9fdcc6; }
.v5 .back { --k1: #2e1a2c; --k2: #120912; --ink: #e8b04a; }
.v6 .back { --k1: #3a2412; --k2: #160c06; --ink: #ffc27a; }
.v7 .back { --k1: #1c1c28; --k2: #0b0b12; --ink: #f3eaff; }
.v8 .back { --k1: #2a1c38; --k2: #0e0916; --ink: #d9c6f2; }
.v9 .back { --k1: #2e100a; --k2: #0e0402; --ink: #ff9d4d; }

:global(:root[data-theme='light']) .v0 .back { --k1: #f6ead4; --k2: #e3cba4; --ink: #8a6a3f; }
:global(:root[data-theme='light']) .v1 .back { --k1: #faf3e6; --k2: #e9d7b9; --ink: #9b7646; }
:global(:root[data-theme='light']) .v2 .back { --k1: #eceaf4; --k2: #cfcae2; --ink: #55508a; }
:global(:root[data-theme='light']) .v3 .back { --k1: #fdf0d6; --k2: #f1c88b; --ink: #a85d16; }
:global(:root[data-theme='light']) .v4 .back { --k1: #e6f2ec; --k2: #c0dccd; --ink: #2f7a58; }
:global(:root[data-theme='light']) .v5 .back { --k1: #f5e8e4; --k2: #e3c6c0; --ink: #965a52; }
:global(:root[data-theme='light']) .v6 .back { --k1: #fde6c6; --k2: #f1b98a; --ink: #a2551b; }
:global(:root[data-theme='light']) .v7 .back { --k1: #f1f0f6; --k2: #d9d6e6; --ink: #4a4466; }
:global(:root[data-theme='light']) .v8 .back { --k1: #efe8f4; --k2: #d4c7e2; --ink: #6f528f; }
:global(:root[data-theme='light']) .v9 .back { --k1: #fae2d6; --k2: #eeb59c; --ink: #ab3f22; }

.v0.go .halo { animation: halo 1.4s 0.35s ease-out; }
.v0.go:not(.done) .lift { transform: translateY(-10px) scale(1.06); }

.v1 .back {
  background:
    radial-gradient(circle at 1px 1px, color-mix(in srgb, var(--ink) 30%, transparent) 1px, transparent 1.4px) 0 0 / 9px 9px,
    radial-gradient(120% 80% at 50% 38%, var(--k1), var(--k2));
}
.v1.go .lift { animation: shake 0.38s linear; }
.v1 .card { transition-duration: 0.8s; }
.v1.go .halo { animation: halo 1.3s 0.3s ease-out; }

@keyframes shake {
  20% { transform: translateX(-3px) rotate(-1.5deg); }
  40% { transform: translateX(3px) rotate(1.5deg); }
  60% { transform: translateX(-2px) rotate(-1deg); }
  80% { transform: translateX(2px) rotate(1deg); }
}

.cl { stroke-dasharray: 101; stroke-dashoffset: 101; transition: stroke-dashoffset 0.35s ease-out; }
.go .cl { stroke-dashoffset: 0; }
.st { animation: tw 2.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
.st:nth-child(2n) { animation-delay: -1.2s; }
.st:nth-child(3n) { animation-delay: -0.6s; }
.go .st { animation: none; filter: drop-shadow(0 0 3px var(--ink)); }
.v2 .card { transition-duration: 0.9s; }
.v2.go .halo { animation: halo 1.4s 0.4s ease-out; }

@keyframes tw {
  50% { opacity: 0.35; transform: scale(0.6); }
}

.v3.go .lift { animation: pop 0.9s cubic-bezier(0.3, 1.4, 0.5, 1); }
.v3 .card { transition-duration: 0.55s; transition-timing-function: cubic-bezier(0.6, 0, 0.3, 1); }
.v3.go .flash { animation: flash 0.7s 0.22s ease-out; }
.v3.go .ring { animation: ring 0.8s 0.25s ease-out; }
.sun { transform-box: view-box; transform-origin: 50% 50%; animation: spin 40s linear infinite; }

@keyframes pop {
  18% { transform: scale(0.9); }
  45% { transform: scale(1.14); }
}

.orb { transform-box: view-box; transform-origin: 50% 50%; animation: spin 14s linear infinite; }
.o2 { animation-duration: 9s; animation-direction: reverse; }
.o3 { animation-duration: 6s; }
.go .orb { animation-duration: 0.7s; }
.go .o2 { animation-duration: 0.5s; }
.go .o3 { animation-duration: 0.35s; }
.v4 .ring { width: 160%; height: auto; aspect-ratio: 1; left: -30%; top: 50%; margin: -80% 0 0; border: 1px dashed var(--accent); box-shadow: none; }
.v4.go .ring { animation: orbitRing 1.6s 0.2s ease-out; }

@keyframes orbitRing {
  0% { opacity: 0; transform: scale(0.4) rotate(0); }
  40% { opacity: 1; }
  100% { opacity: 0; transform: scale(1.15) rotate(200deg); }
}

.v5 .front { transform: rotateX(180deg); }
.v5.open .card { transform: rotateX(180deg); }
.v5 .card { transition: transform 1.1s cubic-bezier(0.65, 0, 0.35, 1); }
.v5 .sheen { background: linear-gradient(115deg, transparent 30%, rgba(255, 248, 225, 0.75) 48%, transparent 62%) 0 0 / 300% 100%; }
.v5.go .sheen { animation: sweep 1.3s 0.2s ease-in-out; }
.v5.go .halo { animation: halo 1.4s 0.45s ease-out; }

@keyframes sweep {
  0% { opacity: 1; background-position: 120% 0; }
  100% { opacity: 1; background-position: -40% 0; }
}

.v6.go:not(.done) .lift { transform: translateY(-18px) scale(1.06); }
.v6.go .rays { animation: rays 2.4s ease-in-out; }
.v6 .card { transition-duration: 1.3s; transition-timing-function: cubic-bezier(0.5, 0, 0.2, 1); }

@keyframes rays {
  0% { opacity: 0; transform: rotate(0); }
  30% { opacity: 1; }
  75% { opacity: 0.8; }
  100% { opacity: 0; transform: rotate(60deg); }
}

.foil {
  position: absolute;
  inset: 0;
  mix-blend-mode: color-dodge;
  opacity: 0.55;
  background: conic-gradient(from var(--fc-f, 30deg) at 50% 40%, #ff6fb5, #ffd36e, #7dffb2, #6fd2ff, #b18cff, #ff6fb5);
  animation: foil 8s linear infinite;
}
:global(:root[data-theme='light']) .foil { mix-blend-mode: multiply; opacity: 0.35; }

@property --fc-f {
  syntax: '<angle>';
  inherits: false;
  initial-value: 30deg;
}

@keyframes foil {
  to { --fc-f: 390deg; }
}

.v7 .sheen {
  background: linear-gradient(110deg, transparent 25%, rgba(255, 111, 181, 0.45) 40%, rgba(255, 211, 110, 0.5) 48%, rgba(125, 255, 178, 0.45) 56%, rgba(111, 210, 255, 0.45) 64%, transparent 78%) 0 0 / 300% 100%;
  mix-blend-mode: screen;
}
.v7.go .sheen { animation: sweep 1.2s 0.4s ease-in-out; }

.v8 .card { transition: transform 1.6s cubic-bezier(0.2, 0.7, 0.1, 1); }
.v8.open .card { transform: rotateY(540deg); }
.blob { position: absolute; border-radius: 50%; filter: blur(14px); background: color-mix(in srgb, var(--ink) 40%, transparent); animation: drift 7s ease-in-out infinite alternate; }
.b1 { width: 66%; height: 29%; left: -11%; top: 13%; }
.b2 { width: 78%; height: 32%; right: -22%; top: 48%; animation-delay: -3s; }
.b3 { width: 61%; height: 25%; left: 11%; bottom: -3%; animation-delay: -5s; }
.v8.go .mist { animation: mist 1.8s ease-out; }

@keyframes drift {
  to { transform: translate(24px, -14px) scale(1.15); }
}

@keyframes mist {
  0% { opacity: 0; transform: scale(0.6); }
  30% { opacity: 1; }
  100% { opacity: 0; transform: scale(1.6); }
}

.crack { stroke-dasharray: 240; stroke-dashoffset: 240; opacity: 0; filter: drop-shadow(0 0 4px #ffd36e); }
.go .crack { animation: crack 0.35s ease-in forwards; }
.go .c2 { animation-delay: 0.08s; }
.go .c3 { animation-delay: 0.15s; }
.v9.go .lift { animation: rumble 0.5s linear; }
.v9 .card { transition: transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1); }
.v9.go .flash { animation: flash 0.8s 0.42s ease-out; }
.v9.go .ring { animation: ring 0.9s 0.45s ease-out; border-color: #ffb35c; box-shadow: 0 0 22px #ff8a3d; }

@keyframes crack {
  to { stroke-dashoffset: 0; opacity: 1; }
}

@keyframes rumble {
  10% { transform: translate(-1px, 1px); }
  20% { transform: translate(2px, -1px) rotate(0.6deg); }
  30% { transform: translate(-2px, 2px); }
  40% { transform: translate(3px, 0) rotate(-1deg); }
  50% { transform: translate(-3px, -2px); }
  60% { transform: translate(4px, 2px) rotate(1.2deg); }
  70% { transform: translate(-4px, -1px); }
  80% { transform: translate(4px, -2px) rotate(-1.4deg); }
  90% { transform: translate(-3px, 2px) scale(1.04); }
}

@media (prefers-reduced-motion: reduce) {
  .lit .bridge {
    animation: none;
  }
}
